import { ShieldX, MapPin, Wifi, Globe, AlertTriangle } from 'lucide-react';

interface AccessDeniedPageProps {
  blockInfo: {
    country: string;
    countryCode: string;
    isVpn: boolean;
    reason: string;
  };
}

export const AccessDeniedPage = ({ blockInfo }: AccessDeniedPageProps) => {
  const isVpnBlocked = blockInfo.isVpn;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-900/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-2xl w-full">
        {/* Main card */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-red-500/20 rounded-3xl p-8 md:p-12 shadow-2xl shadow-red-900/20">
          {/* Icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-red-500 to-orange-600 p-6 rounded-full">
                <ShieldX className="w-16 h-16 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-red-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
            Toegang Geweigerd
          </h1>

          {/* Subtitle */}
          <p className="text-xl text-center text-slate-300 mb-8">
            Deze website is alleen toegankelijk vanuit Nederland
          </p>

          {/* Info boxes */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {/* Location info */}
            <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600/50">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="w-5 h-5 text-red-400" />
                <span className="text-slate-400 text-sm">Gedetecteerde locatie</span>
              </div>
              <p className="text-white font-semibold text-lg">
                {blockInfo.country} ({blockInfo.countryCode})
              </p>
            </div>

            {/* VPN/Status info */}
            <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600/50">
              <div className="flex items-center gap-3 mb-2">
                {isVpnBlocked ? (
                  <Wifi className="w-5 h-5 text-orange-400" />
                ) : (
                  <Globe className="w-5 h-5 text-red-400" />
                )}
                <span className="text-slate-400 text-sm">Status</span>
              </div>
              <p className="text-white font-semibold text-lg">
                {isVpnBlocked ? 'VPN/Proxy Gedetecteerd' : 'Niet-Nederlandse locatie'}
              </p>
            </div>
          </div>

          {/* Warning message */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 mb-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-red-400 font-semibold mb-1">Waarom geen toegang?</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {isVpnBlocked ? (
                    <>
                      We hebben gedetecteerd dat je een VPN of proxy gebruikt. 
                      Om misbruik te voorkomen en de integriteit van onze diensten te waarborgen, 
                      is toegang via VPN/proxy niet toegestaan.
                    </>
                  ) : (
                    <>
                      TheorieAanbieders.nl is een Nederlandse dienst specifiek voor het Nederlandse 
                      rijbewijs- en verkeerssysteem. Onze content, rijscholen en diensten zijn alleen 
                      relevant voor gebruikers in Nederland.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Dutch flag decoration */}
          <div className="flex justify-center gap-1 mb-6">
            <div className="h-2 w-16 bg-red-600 rounded-full"></div>
            <div className="h-2 w-16 bg-white rounded-full"></div>
            <div className="h-2 w-16 bg-blue-600 rounded-full"></div>
          </div>

          {/* Footer text */}
          <p className="text-center text-slate-500 text-sm">
            Bevind je je in Nederland en zie je deze melding ten onrechte?{' '}
            <br className="md:hidden" />
            Schakel je VPN uit en probeer het opnieuw.
          </p>
        </div>

        {/* Bottom decoration */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-xs">
            © {new Date().getFullYear()} TheorieAanbieders.nl - Nederlands Platform voor Rijscholen
          </p>
        </div>
      </div>
    </div>
  );
};
